/**
 * TanStack Query factory functions for products + clinic_stock.
 * Used by the OTS picker step and the prescription wizard's product dropdown.
 */

import type { SupabaseClient } from '@supabase/supabase-js';
import type { Database } from '@insolech/db';

export const productsKeys = {
  all: ['products'] as const,
  lists: () => [...productsKeys.all, 'list'] as const,
  list: (params: ProductListParams) => [...productsKeys.lists(), params] as const,
};

export const clinicStockKeys = {
  all: ['clinic_stock'] as const,
  byClinic: (clinicId: string) => [...clinicStockKeys.all, 'clinic', clinicId] as const,
};

export interface ProductListParams {
  /** Filter to a specific line family. Pass `null` for all. */
  lineType?: string | null;
  /** Filter for OTS picker only. */
  family?: 'custom' | 'ots' | 'all';
  /** Hide inactive (default true). */
  activeOnly?: boolean;
}

const CUSTOM_LINE_TYPES = [
  'custom_insole',
  'premium_3d_insole',
  'orthopedic_sandal',
  'sandal_insole_combo',
];
const OTS_LINE_TYPES = ['ots_insole', 'other_equipment'];

export interface ProductRow {
  id: string;
  sku: string;
  line_type: string;
  category: string;
  name_en: string;
  description_en: string | null;
  default_image_url: string | null;
  is_active: boolean;
  display_order: number;
}

export function productsListQuery(client: SupabaseClient<Database>, params: ProductListParams) {
  return {
    queryKey: productsKeys.list(params),
    queryFn: async (): Promise<ProductRow[]> => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let q: any = client
        .from('products')
        .select(
          'id, sku, line_type, category, name_en, description_en, default_image_url, is_active, display_order',
        );
      if (params.activeOnly !== false) q = q.eq('is_active', true);
      if (params.lineType) q = q.eq('line_type', params.lineType);
      else if (params.family === 'custom') q = q.in('line_type', CUSTOM_LINE_TYPES);
      else if (params.family === 'ots') q = q.in('line_type', OTS_LINE_TYPES);
      q = q.order('display_order', { ascending: true });
      const { data, error } = await q;
      if (error) throw error;
      return ((data ?? []) as unknown as ProductRow[]) ?? [];
    },
  };
}

export interface ClinicStockRow {
  id: string;
  clinic_id: string;
  product_id: string;
  size: string;
  quantity_on_hand: number;
  reorder_threshold: number;
  product?: { id: string; name_en: string; sku: string; line_type: string } | null;
}

export function clinicStockQuery(client: SupabaseClient<Database>, clinicId: string) {
  return {
    queryKey: clinicStockKeys.byClinic(clinicId),
    queryFn: async (): Promise<ClinicStockRow[]> => {
      const { data, error } = await client
        .from('clinic_stock')
        .select(
          'id, clinic_id, product_id, size, quantity_on_hand, reorder_threshold,' +
            ' product:products(id, name_en, sku, line_type)',
        )
        .eq('clinic_id', clinicId);
      if (error) throw error;
      const rows = (data ?? []) as unknown as ClinicStockRow[];
      return rows.sort((a, b) => {
        const an = a.product?.name_en ?? '';
        const bn = b.product?.name_en ?? '';
        if (an !== bn) return an.localeCompare(bn);
        return a.size.localeCompare(b.size);
      });
    },
  };
}

export interface PrescriptionModificationRow {
  id: string;
  order_line_id: string;
  region: string;
  modification_type: string;
  intensity: string | null;
  parameters: Record<string, unknown>;
  source: string;
  created_at: string;
}

/**
 * Get persisted prescription modifications for an order line. Used by the
 * "View full prescription" drawer when rendering historical orders.
 */
export async function getOrderLineModifications(
  client: SupabaseClient<Database>,
  orderLineId: string,
): Promise<PrescriptionModificationRow[]> {
  const { data, error } = await client
    .from('order_line_modifications')
    .select('id, order_line_id, region, modification_type, intensity, parameters, source, created_at')
    .eq('order_line_id', orderLineId)
    .order('created_at', { ascending: true });
  if (error) throw error;
  return ((data ?? []) as unknown as PrescriptionModificationRow[]) ?? [];
}
